import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import {
  LocalizationProvider,
  PickerChangeHandlerContext,
  DateTimePicker,
  DateTimeValidationError,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { getLocale } from '@/components/common/translateFunctions';
import { CustomDatePickerHeader } from './CustomDatePickerHeader';
import { localeText } from './DateRangeSelectBase';
import { isValidDate } from '@/utilities/dateUtils';

import './DateTimeSelect.css';

export function DateTimePickerSelect({
  sx,
  label,
  minDate = new Date('2010-01-01T00:00:00Z'),
  maxDate = new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
  disabled = false,
  currentValue,
  setSelectedDate,
}: {
  sx?: object;
  label: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  currentValue: Date | null;
  setSelectedDate: (date: Date) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const acceptedValue = useRef<Date | null | undefined>(undefined);
  const [displayValue, setDisplayValue] = useState<Date | null>(currentValue);

  function onAccept(newValue: Date | null, context: PickerChangeHandlerContext<DateTimeValidationError>) {
    if (!newValue) return;
    if (!isValidDate(newValue)) return;

    let date: Date = newValue;

    if (date < minDate) date = minDate;
    if (date > maxDate) date = maxDate;

    if (context.source === 'view') {
      // If selected via mouse/touch, trigger immediately
      setSelectedDate(date);
    } else {
      // Otherwise store for submit
      acceptedValue.current = date;
    }
  }

  function submit() {
    if (!acceptedValue.current) return;
    setSelectedDate(acceptedValue.current);
    setDisplayValue(acceptedValue.current);
    acceptedValue.current = undefined; // Prevent double submission
  }

  function onChange(newValue: Date | null) {
    setDisplayValue(newValue);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DateTimePicker
        label={label}
        closeOnSelect={true}
        className="mui-datetime-selector"
        sx={sx}
        value={displayValue}
        onChange={onChange}
        onAccept={onAccept}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        slots={{
          calendarHeader: CustomDatePickerHeader,
        }}
        slotProps={{
          field: {
            id: 'date_picker_panel',
            onBlur: submit,
            onKeyDown: (event) => {
              if (event.code === 'Enter' || event.code === 'NumpadEnter') submit();
            },
          },
          textField: {
            placeholder: 'YYYY.MM.DD. hh:mm',
            inputProps: { 'data-qa-element-id': 'single_date_input' },
          },
          popper: {
            id: 'date_picker_popup',
          },
          openPickerButton: {
            id: 'open_calendar_picker_button',
          },
          day: (ownerState) => {
            const d = ownerState.day;
            const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            return { id: `day_of_month_${formatted}` };
          },
          calendarHeader: {
            maxDate: maxDate,
          } as Partial<PickersCalendarHeaderProps>,
        }}
        localeText={localeText(t)}
      />
    </LocalizationProvider>
  );
}
